import React, { useState, useEffect } from "react";
import EditField from "../InlineEdit/EditField";
import moment from "moment";

// Helper to extract a field value from rows data with support for nested/casing/_id variations
export const getRowValue = (rows, key) => {
  if (!rows || !key) return undefined;

  // Handle dot notation (e.g. "category._id")
  if (typeof key === "string" && key.includes(".")) {
    const parts = key.split(".");
    let current = rows;
    for (const part of parts) {
      if (current === null || current === undefined) {
        current = undefined;
        break;
      }
      current =
        current[part] !== undefined
          ? current[part]
          : current[part.toLowerCase()];
    }
    if (current !== undefined && current !== null) return current;
  }

  // Direct key check
  if (rows[key] !== undefined && rows[key] !== null) return rows[key];

  // In rows.rows
  if (rows.rows && rows.rows[key] !== undefined && rows.rows[key] !== null) {
    return rows.rows[key];
  }

  // Case-insensitive check on top-level
  const lowerKey = String(key).toLowerCase();
  const foundKey = Object.keys(rows).find((k) => k.toLowerCase() === lowerKey);
  if (foundKey && rows[foundKey] !== undefined && rows[foundKey] !== null) {
    return rows[foundKey];
  }

  // Case-insensitive check on rows.rows
  if (rows.rows && typeof rows.rows === "object") {
    const foundNestedKey = Object.keys(rows.rows).find(
      (k) => k.toLowerCase() === lowerKey,
    );
    if (
      foundNestedKey &&
      rows.rows[foundNestedKey] !== undefined &&
      rows.rows[foundNestedKey] !== null
    ) {
      return rows.rows[foundNestedKey];
    }
  }

  // Fallback for _id suffix (e.g. category vs category_id)
  const idKey = key.endsWith("_id") ? key.replace(/_id$/, "") : `${key}_id`;
  if (rows[idKey] !== undefined && rows[idKey] !== null) return rows[idKey];
  if (
    rows.rows &&
    rows.rows[idKey] !== undefined &&
    rows.rows[idKey] !== null
  ) {
    return rows.rows[idKey];
  }

  return undefined;
};

// Helper to recursively normalize row / condition values to string arrays for comparison
export const extractRawValues = (val) => {
  if (val === null || val === undefined) return [];
  if (Array.isArray(val)) {
    return val.reduce((acc, item) => acc.concat(extractRawValues(item)), []);
  }
  if (typeof val === "object") {
    const extracted =
      val._id !== undefined
        ? val._id
        : val.id !== undefined
          ? val.id
          : val.value !== undefined
            ? val.value
            : val.key !== undefined
              ? val.key
              : val;
    if (typeof extracted === "object" && extracted !== null) {
      return [String(extracted)];
    }
    return [String(extracted).trim()];
  }
  return [String(val).trim()];
};

// Evaluates a single condition { key, operator, value } against rows
export const evaluateCondition = (condition, rows) => {
  if (!condition || !condition.key) return true;
  const { key, operator, value: condVal } = condition;
  const rowVal = getRowValue(rows, key);

  const op = (operator || "eq").toLowerCase().trim();
  const rawRowVals = extractRawValues(rowVal);
  const rawCondVals = extractRawValues(condVal);

  switch (op) {
    case "in": {
      if (rawRowVals.length === 0) return false;
      const condSet = new Set(rawCondVals.map((v) => v.toLowerCase()));
      return rawRowVals.some((rv) => condSet.has(rv.toLowerCase()));
    }
    case "not_in":
    case "notin":
    case "nin": {
      if (rawRowVals.length === 0) return true;
      const condSet = new Set(rawCondVals.map((v) => v.toLowerCase()));
      return !rawRowVals.some((rv) => condSet.has(rv.toLowerCase()));
    }
    case "eq":
    case "==":
    case "===": {
      if (rawRowVals.length === 0) {
        return rawCondVals.length === 0;
      }
      if (rawCondVals.length === 0) return false;
      const condSet = new Set(rawCondVals.map((v) => v.toLowerCase()));
      return rawRowVals.some((rv) => condSet.has(rv.toLowerCase()));
    }
    case "ne":
    case "neq":
    case "!=":
    case "!==": {
      if (rawRowVals.length === 0) {
        return rawCondVals.length > 0;
      }
      if (rawCondVals.length === 0) return true;
      const condSet = new Set(rawCondVals.map((v) => v.toLowerCase()));
      return !rawRowVals.some((rv) => condSet.has(rv.toLowerCase()));
    }
    case "gt":
    case ">": {
      if (rawRowVals.length === 0 || rawCondVals.length === 0) return false;
      const nRow = Number(rawRowVals[0]);
      const nCond = Number(rawCondVals[0]);
      if (!isNaN(nRow) && !isNaN(nCond)) return nRow > nCond;
      return rawRowVals[0] > rawCondVals[0];
    }
    case "gte":
    case ">=": {
      if (rawRowVals.length === 0 || rawCondVals.length === 0) return false;
      const nRow = Number(rawRowVals[0]);
      const nCond = Number(rawCondVals[0]);
      if (!isNaN(nRow) && !isNaN(nCond)) return nRow >= nCond;
      return rawRowVals[0] >= rawCondVals[0];
    }
    case "lt":
    case "<": {
      if (rawRowVals.length === 0 || rawCondVals.length === 0) return false;
      const nRow = Number(rawRowVals[0]);
      const nCond = Number(rawCondVals[0]);
      if (!isNaN(nRow) && !isNaN(nCond)) return nRow < nCond;
      return rawRowVals[0] < rawCondVals[0];
    }
    case "lte":
    case "<=": {
      if (rawRowVals.length === 0 || rawCondVals.length === 0) return false;
      const nRow = Number(rawRowVals[0]);
      const nCond = Number(rawCondVals[0]);
      if (!isNaN(nRow) && !isNaN(nCond)) return nRow <= nCond;
      return rawRowVals[0] <= rawCondVals[0];
    }
    case "contains": {
      if (rawRowVals.length === 0) return false;
      const needle = (rawCondVals[0] || "").toLowerCase();
      return rawRowVals.some((rv) => rv.toLowerCase().includes(needle));
    }
    case "not_contains":
    case "notcontains": {
      if (rawRowVals.length === 0) return true;
      const needle = (rawCondVals[0] || "").toLowerCase();
      return !rawRowVals.some((rv) => rv.toLowerCase().includes(needle));
    }
    case "exists":
    case "not_empty":
    case "is_not_empty": {
      return rawRowVals.length > 0 && rawRowVals.some((v) => v !== "");
    }
    case "not_exists":
    case "empty":
    case "is_empty": {
      return rawRowVals.length === 0 || rawRowVals.every((v) => v === "");
    }
    default: {
      if (rawRowVals.length === 0 && rawCondVals.length === 0) return true;
      const condSet = new Set(rawCondVals.map((v) => v.toLowerCase()));
      return rawRowVals.some((rv) => condSet.has(rv.toLowerCase()));
    }
  }
};

// Check if all conditions pass (or default to true if condition is not there)
export const checkConditions = (conditions, rows) => {
  if (!conditions) {
    return true; // if condition not there show default
  }
  if (conditions.logic && Array.isArray(conditions.conditions)) {
    if (conditions.logic.toLowerCase() === "or") {
      return conditions.conditions.some((cond) =>
        evaluateCondition(cond, rows),
      );
    }
    return conditions.conditions.every((cond) => evaluateCondition(cond, rows));
  }
  const condList = Array.isArray(conditions) ? conditions : [conditions];
  if (condList.length === 0) {
    return true; // if condition not there show default
  }
  return condList.every((cond) => evaluateCondition(cond, rows));
};

// Determines which dependency fields should be displayed based on conditions and rows
export const getVisibleDependencies = (dependencies, rows, statusItem) => {
  // If statusItem has conditions, verify them first
  if (statusItem?.conditions && !checkConditions(statusItem.conditions, rows)) {
    return {};
  }

  // If dependencies has top-level conditions, verify them
  if (
    dependencies?.conditions &&
    !checkConditions(dependencies.conditions, rows)
  ) {
    return {};
  }

  const defaultDependencies = {
    impact_analysis: { can_empty: true },
    architecture_diagram: { can_empty: true },
  };

  const rawDeps =
    dependencies && Object.keys(dependencies).length > 0
      ? dependencies
      : defaultDependencies;

  const visible = {};
  Object.entries(rawDeps).forEach(([field, config]) => {
    if (field === "conditions") return;

    // If condition not there, show default; if condition is there, check condition
    if (
      !config ||
      !config.conditions ||
      checkConditions(config.conditions, rows)
    ) {
      visible[field] = config || {};
    }
  });

  return visible;
};

function Dependencies(props) {
  const {
    dependencies,
    close,
    status,
    item,
    board_attributes,
    boarddata,
    users,
    rows,
    onSubmit,
  } = props;

  const currentItem = status || item;

  // Filter dependencies based on conditions and current row
  const activeDependencies = getVisibleDependencies(
    dependencies,
    rows,
    currentItem,
  );

  // Helper to format field name into readable title
  const formatFieldLabel = (field) => {
    if (!field) return "";
    return field
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper to find attribute definition in board_attributes
  const getAttributeDef = (fieldName) => {
    if (board_attributes) {
      if (Array.isArray(board_attributes)) {
        const found = board_attributes.find((attr) => attr.field === fieldName);
        if (found) return found;
      } else if (typeof board_attributes === "object") {
        for (const key of Object.keys(board_attributes)) {
          if (Array.isArray(board_attributes[key])) {
            const found = board_attributes[key].find(
              (attr) => attr.field === fieldName,
            );
            if (found) return found;
          }
        }
      }
    }
    // Fallback definition if not found in board_attributes
    return {
      field: fieldName,
      headerName: formatFieldLabel(fieldName),
      attribute_type: "url",
      is_required: true,
    };
  };

  // Helper to extract existing row value
  const getExistingRowValue = (field) => {
    if (!rows) return "";
    if (rows[field] !== undefined && rows[field] !== null) {
      return rows[field];
    }
    if (
      rows.rows &&
      rows.rows[field] !== undefined &&
      rows.rows[field] !== null
    ) {
      return rows.rows[field];
    }
    const lower = field.toLowerCase();
    const foundKey = Object.keys(rows).find((k) => k.toLowerCase() === lower);
    if (foundKey && rows[foundKey] !== undefined && rows[foundKey] !== null) {
      return rows[foundKey];
    }
    return "";
  };

  // Helper to initialize state from rows
  const getInitialStateFromRows = () => {
    const initialData = {};
    const initialNA = {};
    const initialErrors = {};

    Object.keys(activeDependencies).forEach((field) => {
      const attrDef = getAttributeDef(field);
      const existingVal = getExistingRowValue(field);
      const isValNA =
        typeof existingVal === "string" &&
        (existingVal.trim().toUpperCase() === "NA" ||
          existingVal.trim().toUpperCase() === "N/A");

      if (isValNA && attrDef.attribute_type !== "toggle") {
        initialNA[field] = true;
        initialData[field] = "";
      } else {
        initialNA[field] = false;
        if (attrDef.attribute_type === "toggle") {
          initialData[field] =
            existingVal === true ||
            existingVal === 1 ||
            existingVal === "1" ||
            existingVal === "true";
        } else {
          initialData[field] =
            existingVal !== undefined && existingVal !== null
              ? existingVal
              : "";
        }
      }
      initialErrors[field] = { isValid: true, message: "" };
    });

    return { initialData, initialNA, initialErrors };
  };

  // Async call helper for external_api
  const erAsyncCall = (api, evt) => {
    let url = api || "";
    url += evt ? evt : "";
    return new Promise((resolve) => {
      setTimeout(async () => {
        if (evt) {
          try {
            const resp = await fetch(url);
            const data = await resp.json();
            resolve(data);
          } catch (e) {
            resolve([]);
          }
        } else {
          resolve([]);
        }
      }, 300);
    });
  };

  // Form State initialized with prefilled values from rows
  const [formData, setFormData] = useState(
    () => getInitialStateFromRows().initialData,
  );
  const [naState, setNaState] = useState(
    () => getInitialStateFromRows().initialNA,
  );
  const [errors, setErrors] = useState(
    () => getInitialStateFromRows().initialErrors,
  );
  const [submitting, setSubmitting] = useState(false);

  // Sync form state whenever rows, status or dependencies change
  useEffect(() => {
    const { initialData, initialNA, initialErrors } = getInitialStateFromRows();
    setFormData(initialData);
    setNaState(initialNA);
    setErrors(initialErrors);
  }, [dependencies, rows, status, item]);

  // Validation function
  const validateField = (fieldName, attributeType, value, isNA, attrDef) => {
    if (isNA) {
      return { isValid: true, message: "" };
    }

    if (attributeType === "toggle") {
      return { isValid: true, message: "" };
    }

    const label = attrDef?.headerName || formatFieldLabel(fieldName);
    const valStr =
      value !== null && value !== undefined ? String(value).trim() : "";

    if (!valStr) {
      return { isValid: false, message: `${label} is required` };
    }

    if (attributeType === "url") {
      const urlRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
      if (!urlRegex.test(valStr)) {
        return { isValid: false, message: `${label} is invalid` };
      }
    }

    if (attributeType === "number") {
      if (isNaN(valStr) || valStr.includes("-")) {
        return { isValid: false, message: "Please enter a valid number" };
      }
    }

    return { isValid: true, message: "" };
  };

  // Handle Mark as NA checkbox change
  const handleNAToggle = (fieldName, checked) => {
    const attrDef = getAttributeDef(fieldName);
    setNaState((prev) => ({
      ...prev,
      [fieldName]: checked,
    }));

    if (checked) {
      // Set empty value when NA is selected
      setFormData((prev) => ({
        ...prev,
        [fieldName]: attrDef.attribute_type === "toggle" ? false : "",
      }));
      setErrors((prev) => ({
        ...prev,
        [fieldName]: { isValid: true, message: "" },
      }));
    } else {
      // Restore prefilled value from rows if available
      const existingVal = getExistingRowValue(fieldName);
      const isValNA =
        typeof existingVal === "string" &&
        (existingVal.trim().toUpperCase() === "NA" ||
          existingVal.trim().toUpperCase() === "N/A");
      if (
        !isValNA &&
        existingVal !== undefined &&
        existingVal !== null &&
        existingVal !== ""
      ) {
        setFormData((prev) => ({
          ...prev,
          [fieldName]:
            attrDef.attribute_type === "toggle"
              ? existingVal === true ||
                existingVal === 1 ||
                existingVal === "1" ||
                existingVal === "true"
              : existingVal,
        }));
      }
    }
  };

  // Handle input change from EditField
  const handleFieldChange = (fieldName, attributeType, ev, fName, isMulti) => {
    let value = "";
    if (attributeType === "toggle") {
      if (typeof ev === "boolean") {
        value = ev;
      } else if (ev && ev.target) {
        value =
          ev.target.checked !== undefined
            ? ev.target.checked
            : ev.target.value === "true" || ev.target.value === true;
      } else {
        const current = formData[fieldName];
        value = !(current === true || current === "true" || current === 1);
      }
    } else if (
      attributeType === "text" ||
      attributeType === "number" ||
      attributeType === "url" ||
      attributeType === "name"
    ) {
      value = ev && ev.target !== undefined ? ev.target.value : ev;
    } else if (
      attributeType === "select" ||
      attributeType === "category" ||
      attributeType === "priority"
    ) {
      if (isMulti) {
        value = Array.isArray(ev) ? ev.map((item) => item.value) : [];
      } else {
        value = ev ? ev.value : "";
      }
    } else if (
      attributeType === "api" ||
      attributeType === "release" ||
      attributeType === "external_api" ||
      attributeType === "pod_department"
    ) {
      if (isMulti) {
        value = Array.isArray(ev) ? ev.map((item) => item.value) : [];
      } else {
        value = ev ? ev.value : "";
      }
    } else if (attributeType === "people") {
      value = ev ? ev.value : "";
    } else if (attributeType === "date") {
      value = ev
        ? typeof ev === "string"
          ? ev
          : moment(ev).format("YYYY-MM-DD")
        : "";
    } else {
      value =
        ev && ev.target !== undefined
          ? ev.target.value
          : ev
            ? ev.value || ev
            : "";
    }

    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    const attrDef = getAttributeDef(fieldName);
    const validation = validateField(
      fieldName,
      attributeType,
      value,
      naState[fieldName],
      attrDef,
    );
    setErrors((prev) => ({
      ...prev,
      [fieldName]: validation,
    }));
  };

  // Form submit handler
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    let allValid = true;
    const newErrors = {};

    Object.keys(activeDependencies).forEach((field) => {
      const attrDef = getAttributeDef(field);
      const isNA = !!naState[field];
      const val = formData[field];
      const validation = validateField(
        field,
        attrDef.attribute_type,
        val,
        isNA,
        attrDef,
      );
      newErrors[field] = validation;
      if (!validation.isValid) {
        allValid = false;
      }
    });

    setErrors(newErrors);

    if (!allValid) {
      return;
    }

    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(formData, naState);
      } else {
        if (close) close();
      }
    } catch (err) {
      console.error("Error submitting dependencies:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const dependencyEntries = Object.entries(activeDependencies);

  if (dependencyEntries.length === 0) {
    return (
      <div className="dependencies-container" style={{ padding: "16px 8px" }}>
        <p style={{ textAlign: "center", color: "#666", marginBottom: "16px" }}>
          No dependencies required for this item.
        </p>
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
        >
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={close}
            style={{
              minWidth: "90px",
              borderRadius: "4px",
              borderColor: "#ced4da",
              color: "#333",
              backgroundColor: "#fff",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSubmit && onSubmit({}, {})}
            style={{
              minWidth: "120px",
              borderRadius: "4px",
              backgroundColor: "#0069ff",
              borderColor: "#0069ff",
            }}
          >
            Change Status
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dependencies-container" style={{ padding: "4px 8px" }}>
      <form onSubmit={handleSubmit}>
        {dependencyEntries.map(([field, config]) => {
          const attrDef = getAttributeDef(field);
          const isNA = !!naState[field];
          const canEmpty = config?.can_empty;
          const label = attrDef.headerName || formatFieldLabel(field);
          const fieldError = errors[field];
          const isToggle = attrDef.attribute_type === "toggle";

          return (
            <div
              key={field}
              className="form-group mb-3"
              style={{ marginBottom: "18px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <label
                  style={{
                    fontWeight: 600,
                    color: "#212529",
                    marginBottom: 0,
                    fontSize: "14px",
                  }}
                >
                  {label}{" "}
                  {!isNA && !isToggle && (
                    <span style={{ color: "#d9534f" }}>*</span>
                  )}
                </label>

                {canEmpty && !isToggle && (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: 0,
                      fontSize: "13px",
                      color: "#495057",
                      cursor: "pointer",
                      fontWeight: "normal",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isNA}
                      onChange={(e) => handleNAToggle(field, e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                    Not Required
                  </label>
                )}
              </div>

              <div
                className="dependencies-field-input"
                style={{
                  opacity: isNA ? 0.6 : 1,
                  pointerEvents: isNA ? "none" : "auto",
                }}
              >
                <EditField
                  {...attrDef}
                  field={field}
                  attribute_type={attrDef.attribute_type}
                  rows={{ ...rows, ...formData }}
                  disabled={isNA}
                  placeholder={
                    attrDef.attribute_type === "url"
                      ? "Paste sheet or doc link"
                      : `Enter ${label}`
                  }
                  toggleHandler={(ev) => handleFieldChange(field, "toggle", ev)}
                  onChange={(ev, fName, isMulti) =>
                    handleFieldChange(
                      field,
                      attrDef.attribute_type,
                      ev,
                      fName,
                      isMulti,
                    )
                  }
                  onBlur={() => {
                    const validation = validateField(
                      field,
                      attrDef.attribute_type,
                      formData[field],
                      isNA,
                      attrDef,
                    );
                    setErrors((prev) => ({
                      ...prev,
                      [field]: validation,
                    }));
                  }}
                  newerror={fieldError}
                  errors={fieldError}
                  users={users || {}}
                  boardData={boarddata || {}}
                  {...(attrDef.attribute_type === "external_api" && {
                    getApiCall: erAsyncCall,
                  })}
                />
              </div>

              {/* {fieldError && !fieldError.isValid && !isNA && (
                <span
                  className="error-feedback"
                  style={{
                    color: "#d9534f",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {fieldError.message}
                </span>
              )} */}
            </div>
          );
        })}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "24px",
          }}
        >
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={close}
            style={{
              minWidth: "90px",
              borderRadius: "4px",
              borderColor: "#ced4da",
              color: "#333",
              backgroundColor: "#fff",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            style={{
              minWidth: "120px",
              borderRadius: "4px",
              backgroundColor: "#0069ff",
              borderColor: "#0069ff",
            }}
          >
            {submitting ? "Saving..." : "Change Status"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Dependencies;
